/**
 * Tradução de `LayoutSpec` para CSS — funções puras, testáveis sem DOM.
 *
 * Três decisões, e as três mudam comportamento em relação ao módulo antigo:
 *
 * 1. **Mais de um componente por linha.** O `grid-layout.service.ts` antigo
 *    forçava `col: 0` e um componente por linha, enquanto
 *    `findNextAvailablePosition`, `checkCollision` e `optimizeLayout` estavam
 *    escritos e nunca eram chamados — então toda tela saía como uma coluna
 *    vertical infinita. Aqui a grade de 24 colunas é do CSS: dois campos de 12
 *    ficam lado a lado porque o navegador os empacota, sem serviço no meio.
 *
 * 2. **Ordem pelo `order` do CSS, não pela posição no array.** Reordenar deixa
 *    de exigir mexer no array, o que importa porque o mesmo documento é
 *    desenhado no editor e em produção.
 *
 * 3. **Largura sai como CUSTOM PROPERTY, não como `grid-column` direto.** Esta é
 *    sutil e vale explicar: estilo inline vence regra de CSS, então se o nó
 *    escrevesse `grid-column: span 12` inline, a regra de tela estreita que
 *    manda tudo ocupar a linha inteira precisaria de `!important` para vencer —
 *    e `!important` em cascata é exatamente a doença do módulo antigo (332
 *    ocorrências, 166 só no canvas). Emitindo `--tem-child-span`, o SCSS decide
 *    o que fazer com o número em cada faixa de tela, e ninguém precisa brigar
 *    por especificidade. O padrão já existe no projeto: `getPanelStyles()`
 *    emite `--panel-border-color` do mesmo jeito.
 */
import { GRID_COLUMNS, LayoutSpec, StyleTokens } from '../../model/layout';
import { ScreenNode } from '../../model/node-props';

/** Variáveis aplicadas no elemento que envolve cada filho. */
export type ChildLayoutStyles = Readonly<Record<string, string>>;

/**
 * Largura de um filho dentro da grade de 24 colunas.
 *
 * Sem `colSpan` o nó ocupa a linha inteira. É a escolha conservadora: campo que
 * o usuário não configurou aparece inteiro em vez de espremido, e o inspector
 * oferece as larguras rápidas para reduzir.
 */
export function childLayoutStyles(node: ScreenNode | undefined): ChildLayoutStyles {
  const layout: LayoutSpec | undefined = node?.layout;

  if (layout === undefined) {
    return { '--tem-child-span': String(GRID_COLUMNS), order: '0' };
  }

  const styles: Record<string, string> = {
    '--tem-child-span': String(normalizeSpan(layout.colSpan)),
    order: String(Number.isFinite(layout.order) ? layout.order : 0)
  };

  if (layout.rowSpan !== undefined && Number.isFinite(layout.rowSpan) && layout.rowSpan > 1) {
    styles['--tem-child-row-span'] = String(Math.floor(layout.rowSpan));
  }

  if (node !== undefined && stretchesToRowHeight(node)) {
    styles['--tem-child-stretch'] = '1';
  }

  if (layout.col !== undefined && Number.isFinite(layout.col) && layout.col >= 0) {
    // Coordenada explícita: só surte efeito em container 'grid-free'.
    // O CSS conta colunas a partir de 1; o modelo conta de 0.
    styles['--tem-child-col'] = String(Math.floor(layout.col) + 1);
  }

  return styles;
}

/**
 * O nó acompanha a ALTURA da linha da grade, em vez da própria?
 *
 * A grade dos containers é `align-items: start`, e isso é deliberado: sem ele,
 * um campo de uma linha ao lado de uma grade alta esticaria até a altura da
 * grade, com a caixa de texto gigante e o rótulo perdido no topo.
 *
 * O **divisor vertical** é a exceção legítima. Ele existe para separar o que
 * está à esquerda do que está à direita, e uma risca de 1,5 rem entre dois
 * campos de 4 rem não separa nada — parece defeito. Diferente do textarea, ele
 * não tem altura própria a preservar: a altura dele É a dos vizinhos.
 *
 * Por que a decisão mora aqui, e não no renderer do divisor: quem estica é o
 * INVÓLUCRO, que é o item da grade, e ele pertence ao container. Um componente
 * não alcança o `align-self` do próprio invólucro por CSS — e `:has()` no
 * container não resolveria, porque o encapsulamento emulado do Angular carimba
 * o seletor de dentro com o atributo do componente errado.
 */
export function stretchesToRowHeight(node: ScreenNode): boolean {
  if (node.type !== 'divider') {
    return false;
  }

  return (node.props as { orientation?: string }).orientation === 'vertical';
}

/**
 * Mantém a largura entre 1 e 24, e inteira.
 *
 * Os fixtures de produção têm `colSpan` fracionário e acima de 24. O migrador
 * preserva o valor original de propósito; a correção acontece aqui, na exibição,
 * para não reescrever dado de tela que ninguém pediu para mudar.
 */
export function normalizeSpan(colSpan: number | undefined): number {
  if (colSpan === undefined || !Number.isFinite(colSpan)) {
    return GRID_COLUMNS;
  }

  const rounded = Math.round(colSpan);

  if (rounded < 1) {
    return 1;
  }
  if (rounded > GRID_COLUMNS) {
    return GRID_COLUMNS;
  }
  return rounded;
}

/**
 * Classes de tema de um nó, a partir dos tokens semânticos.
 *
 * Devolve classe, não cor. É o que impede o construtor de furar o design system:
 * o SCSS define o que `tem-surface-raised` significa em cada tema, e o usuário
 * escolhe entre papéis, nunca entre valores hex.
 */
export function styleTokenClasses(tokens: StyleTokens | undefined): Readonly<Record<string, boolean>> {
  const classes: Record<string, boolean> = {};

  if (tokens === undefined) {
    return classes;
  }

  if (tokens.surface !== undefined) {
    classes['tem-surface-' + tokens.surface] = true;
  }
  if (tokens.emphasis !== undefined) {
    classes['tem-emphasis-' + tokens.emphasis] = true;
  }
  if (tokens.intent !== undefined) {
    classes['tem-intent-' + tokens.intent] = true;
  }

  return classes;
}

/**
 * Propriedades aceitas em `style.raw`.
 *
 * O escape hatch precisa existir — as cores hex das telas antigas caem em `raw`
 * na migração e continuam funcionando. Mas `raw` vem do banco e é alcançável
 * pelo editor de JSON, então sem lista um valor colado poderia injetar
 * `position`, `z-index` ou `content` e quebrar o layout da aplicação em volta.
 */
const ALLOWED_RAW_PROPERTIES: readonly string[] = [
  'background-color', 'background',
  'color',
  'border', 'border-color', 'border-width', 'border-style', 'border-radius',
  'font-size', 'font-weight', 'font-style',
  'padding', 'margin',
  'text-align',
  'min-height', 'max-height', 'min-width', 'max-width',
  'box-shadow',
  'opacity'
];

export function safeRawStyles(tokens: StyleTokens | undefined): Readonly<Record<string, string>> {
  const styles: Record<string, string> = {};

  if (tokens === undefined || tokens.raw === undefined) {
    return styles;
  }

  for (const property of ALLOWED_RAW_PROPERTIES) {
    const value = tokens.raw[property];

    if (value === undefined || typeof value !== 'string') {
      continue;
    }
    // `expression(` e `url(javascript:` são vetores antigos, mas recusar custa
    // zero e o valor vem do banco.
    if (/expression\s*\(|javascript:/i.test(value)) {
      continue;
    }

    styles[property] = value;
  }

  return styles;
}
